import type { ReactNode } from 'react';
import { PaginaAyuda, Captura, Pasos } from '../shared/Ayuda.tsx';

export function AyudaAdminPage(): ReactNode {
  return (
    <PaginaAyuda
      titulo="Manual del Panel Admin"
      intro="Acá está explicado, paso a paso y en simple, para qué sirve cada pantalla del panel."
      accent="primario"
      secciones={[
        {
          id: 'inicio',
          titulo: 'Inicio: el pulso del día',
          contenido: (
            <>
              <p>
                La primera pantalla muestra las <strong>alertas</strong> (solicitudes sin cubrir a menos de 48 horas,
                ausencias) y los <strong>dispositivos de confianza</strong> esperando aprobación: cuando alguien del
                personal entra a la app desde un celular nuevo, aparece acá y lo aprobás o rechazás con un toque.
              </p>
              <p>
                También está el interruptor de <strong>asignación automática</strong>: prendido, las convocatorias salen
                solas apenas el cliente crea una solicitud; apagado, cada solicitud espera a que vos la lances.
              </p>
              <Captura src="admin-inicio.png" alt="Pantalla de inicio del panel admin" pie="El inicio con alertas y dispositivos por aprobar." />
            </>
          ),
        },
        {
          id: 'solicitudes',
          titulo: 'Solicitudes: seguir cada evento',
          contenido: (
            <>
              <p>
                Acá vivís el día a día. Cada tarjeta es un evento con su estado:{' '}
                <strong>Pendiente</strong> (faltan confirmaciones), <strong>Confirmada</strong> (todos aceptaron) o{' '}
                <strong>En asignación</strong> (las convocatorias están dando vueltas).
              </p>
              <Pasos
                items={[
                  <>Usá los filtros <strong>Hoy / Esta semana / Semana que viene</strong> para ver lo que importa (arranca en "Esta semana").</>,
                  <>Desplegá una solicitud para ver a cada persona con su horario: desde ahí podés <strong>Reasignar</strong> (la baja y convoca al siguiente) o <strong>Cargar turno</strong> (las horas reales trabajadas).</>,
                  <>Con la lupa buscás por nombre de persona o por el número de la solicitud (el #ID corto).</>,
                  <>El botón de exportar baja la lista a Excel.</>,
                ]}
              />
              <Captura src="admin-solicitudes.png" alt="Lista de solicitudes con estados" pie="Solicitudes de la semana con sus confirmaciones." />
            </>
          ),
        },
        {
          id: 'nueva',
          titulo: 'Nueva solicitud: pedir personal',
          contenido: (
            <>
              <p>
                Elegís cliente, salón, fecha y horario (con los botones 6 h / 8 h / 9 h la hora de fin se calcula sola;
                si el evento termina de madrugada, no pasa nada: el sistema lo entiende).
              </p>
              <p>
                Después marcás cuántas personas de cada categoría. Lo importante: una misma categoría puede tener{' '}
                <strong>varios turnos con distinto horario</strong> — por ejemplo 4 mozos que entran 20:00 y otros 4 a
                las 02:00. Tocá <strong>"＋ Agregar otro turno con distinto horario"</strong> dentro de la categoría.
              </p>
              <Pasos
                items={[
                  <>En cada turno podés elegir gente por nombre: usá el <strong>buscador</strong> (por nombre o cédula) y marcá los casilleros.</>,
                  <>Lo que no elijas a dedo lo asigna el sistema solo, por ranking.</>,
                  <>Antes de crear, revisá el <strong>Resumen del pedido</strong>: muestra turno por turno quién va con ✓ y cuántos salen por asignación automática ⚡.</>,
                ]}
              />
              <Captura src="admin-nueva-turnos.png" alt="Nueva solicitud con dos turnos de mozo" pie="Una categoría con dos turnos y el resumen del pedido." />
            </>
          ),
        },
        {
          id: 'personal',
          titulo: 'Personal: el plantel',
          contenido: (
            <>
              <p>
                La ficha de cada persona: categorías que puede cubrir (una marcada como principal), prioridad,
                disponibilidad semanal y calificación. Para dar de alta a alguien, cargala y mandale la{' '}
                <strong>invitación</strong>: le llega un enlace donde crea su propia cuenta y contraseña.
              </p>
              <p>Nunca borres a nadie: al desactivar, la persona deja de aparecer pero su historial queda.</p>
              <Captura src="admin-personal.png" alt="Lista del personal" pie="El plantel con categorías y prioridades." />
            </>
          ),
        },
        {
          id: 'clientes',
          titulo: 'Clientes: salones y usuarios',
          contenido: (
            <>
              <p>
                Cada cliente tiene sus <strong>salones</strong> (con coordenadas GPS para el check-in — se pegan desde
                Google Maps) y sus <strong>usuarios</strong> del portal.
              </p>
              <Pasos
                items={[
                  <><strong>Editar mail</strong>: cambia el correo con el que entra ese usuario.</>,
                  <><strong>Clave nueva</strong>: genera una contraseña de 8 dígitos que se muestra una sola vez — pasásela al cliente en el momento.</>,
                  <><strong>Eliminar</strong>: para correos viejos que ya no deben entrar.</>,
                ]}
              />
              <Captura src="admin-clientes.png" alt="Ficha de cliente con usuarios" pie="Los usuarios del cliente con sus acciones." />
            </>
          ),
        },
        {
          id: 'recibo',
          titulo: 'Recibo semanal: facturar al cliente',
          contenido: (
            <>
              <p>
                En <strong>Reportes → Recibo para el cliente</strong> elegís cliente y semana, y sale el detalle igual a
                la planilla: fecha, C.I., nombre, entrada, salida, horas, salón, función y cobro, agrupado por evento
                con subtotal, IVA (22 %) y total. Se exporta a Excel o PDF.
              </p>
              <p>
                Cada fila usa las horas <strong>reales</strong> del turno cargado; si un turno no se cargó, muestra un
                estimado con el horario previsto (marcado como estimado).
              </p>
              <Captura src="admin-recibo.png" alt="Recibo semanal para el cliente" pie="El recibo de una semana, listo para exportar." />
            </>
          ),
        },
        {
          id: 'liquidaciones',
          titulo: 'Liquidaciones: pagar al personal',
          contenido: (
            <p>
              Generás la liquidación de un período y el sistema agrupa todos los turnos trabajados por persona, con
              horas normales, nocturnas y ajustes (todo ajuste manual pide motivo y queda auditado). Cuando pagás,
              la marcás como pagada y la persona la ve en su app.
            </p>
          ),
        },
        {
          id: 'qr',
          titulo: 'QR y kiosk: el check-in en el salón',
          contenido: (
            <p>
              Cada cliente tiene un QR dinámico (cambia cada 30 segundos) para que el personal marque llegada y salida.
              Desde la ficha del cliente se abre el <strong>kiosk</strong>: una pantalla con el QR gigante para dejar en
              el salón, protegida con PIN. El personal también puede marcar por GPS desde su app si está dentro del
              radio del salón.
            </p>
          ),
        },
      ]}
    />
  );
}
